// First-person fly controls for the editor viewport. Uses three's
// PointerLockControls for mouse-look (Esc unlocks); WASD for planar movement
// in camera space, Q/E for world up/down, Shift for sprint. Wheel scales the
// base speed (stored locally; surfaced in the viewport overlay).
//
// `enabled` is gated by the gizmo (TransformControls dragging) — when false,
// keyboard input is ignored and the camera doesn't integrate.

import * as THREE from 'three';
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js';

export interface FlyConfig {
  baseSpeed: number;
  sprintMul: number;
  accelTimeSec: number;
  sensitivity: number;
}

const DEFAULTS: FlyConfig = {
  baseSpeed: 8,
  sprintMul: 4,
  accelTimeSec: 0.08,
  sensitivity: 0.0022
};

export class FlyControls {
  readonly camera: THREE.PerspectiveCamera;
  readonly dom: HTMLElement;
  readonly pointer: PointerLockControls;
  enabled = true;
  speedScale = 1;

  private cfg: FlyConfig;
  private keys = new Set<string>();
  private velocity = new THREE.Vector3();
  private targetVel = new THREE.Vector3();
  private tmpForward = new THREE.Vector3();
  private tmpRight = new THREE.Vector3();
  private worldUp = new THREE.Vector3(0, 1, 0);
  private tween: { from: THREE.Vector3; to: THREE.Vector3; lookAt: THREE.Vector3; t0: number; durMs: number } | null = null;

  constructor(camera: THREE.PerspectiveCamera, dom: HTMLElement, cfg: Partial<FlyConfig> = {}) {
    this.camera = camera;
    this.dom = dom;
    this.cfg = { ...DEFAULTS, ...cfg };
    this.pointer = new PointerLockControls(camera, dom);
    this.pointer.pointerSpeed = this.cfg.sensitivity * 1000;

    addEventListener('keydown', this.onKeyDown);
    addEventListener('keyup', this.onKeyUp);
    addEventListener('blur', this.onBlur);
    dom.addEventListener('wheel', this.onWheel, { passive: false });
  }

  /** Request pointer-lock so mouse-look + WASD become active. */
  lock(): void {
    if (!this.pointer.isLocked) this.pointer.lock();
  }

  unlock(): void {
    if (this.pointer.isLocked) this.pointer.unlock();
  }

  isLocked(): boolean { return this.pointer.isLocked; }

  /** Smoothly fly the camera to frame `target` at distance `radius * 2.4`. */
  focusOn(target: THREE.Vector3, radius: number): void {
    const dir = new THREE.Vector3();
    this.camera.getWorldDirection(dir).normalize();
    const dist = Math.max(2, radius * 2.4);
    const to = target.clone().add(dir.clone().multiplyScalar(-dist));
    this.tween = {
      from: this.camera.position.clone(),
      to,
      lookAt: target.clone(),
      t0: performance.now(),
      durMs: 600
    };
  }

  update(dt: number): void {
    if (this.tween) {
      const now = performance.now();
      const t = Math.min(1, (now - this.tween.t0) / this.tween.durMs);
      const e = easeInOutCubic(t);
      this.camera.position.lerpVectors(this.tween.from, this.tween.to, e);
      this.camera.lookAt(this.tween.lookAt);
      if (t >= 1) this.tween = null;
      return;
    }
    if (!this.enabled || !this.pointer.isLocked) {
      this.velocity.set(0, 0, 0);
      this.targetVel.set(0, 0, 0);
      return;
    }
    const sprint = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
    const speed = this.cfg.baseSpeed * this.speedScale * (sprint ? this.cfg.sprintMul : 1);

    let fz = 0, rx = 0, uy = 0;
    if (this.keys.has('KeyW')) fz += 1;
    if (this.keys.has('KeyS')) fz -= 1;
    if (this.keys.has('KeyA')) rx -= 1;
    if (this.keys.has('KeyD')) rx += 1;
    if (this.keys.has('KeyE') || this.keys.has('Space')) uy += 1;
    if (this.keys.has('KeyQ') || this.keys.has('ControlLeft')) uy -= 1;

    const lenSq = fz * fz + rx * rx + uy * uy;
    if (lenSq > 0) {
      const inv = 1 / Math.sqrt(lenSq);
      fz *= inv; rx *= inv; uy *= inv;
    }

    this.camera.getWorldDirection(this.tmpForward);
    this.tmpForward.normalize();
    this.tmpRight.crossVectors(this.tmpForward, this.worldUp).normalize();

    this.targetVel
      .copy(this.tmpForward).multiplyScalar(fz * speed)
      .addScaledVector(this.tmpRight, rx * speed)
      .addScaledVector(this.worldUp, uy * speed);

    const accel = Math.min(1, dt / Math.max(1e-3, this.cfg.accelTimeSec));
    this.velocity.lerp(this.targetVel, accel);
    this.camera.position.addScaledVector(this.velocity, dt);
  }

  setSpeed(scale: number): void { this.speedScale = Math.max(0.05, Math.min(8, scale)); }

  dispose(): void {
    removeEventListener('keydown', this.onKeyDown);
    removeEventListener('keyup', this.onKeyUp);
    removeEventListener('blur', this.onBlur);
    this.dom.removeEventListener('wheel', this.onWheel);
    this.pointer.disconnect();
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.pointer.isLocked) return;
    this.keys.add(e.code);
  };
  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };
  private onBlur = (): void => { this.keys.clear(); };
  private onWheel = (e: WheelEvent): void => {
    if (!this.pointer.isLocked) return;
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    this.setSpeed(this.speedScale * factor);
  };
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
